// Affiliate Program Plan Task 5 — FirstPromoter v2 tracking init for the web/
// marketing site. Mirrors web/app/lib/analytics.ts:
//   - SSR-safe (no-op when window is undefined)
//   - Idempotent (early return if initialized)
//   - Local-dev safe (no-op when the cid is empty so docker-compose web/ boots
//     without a real FP account)
//
// This ports FirstPromoter's v2 install snippet. The CDN file is `fpr.js`
// (NOT the legacy v1 `fprom.js`). fpr.js drains the `window.fpr.q` queue when
// it loads, so the inline queue shim AND the init/click calls must run BEFORE
// the script tag is appended — otherwise nothing is ever tracked.
//
// Once loaded, fpr.js:
//   - Reads `?fpr=<ref_id>` from the URL and writes the _fprom_ref + _fprom_tid
//     cookies (Domain=.hookmyapp.com so they span web/ + frontend/)
//   - Records the click queued via fpr("click")
//   - Leaves window.fpr callable for the trackSignup() referral call (Task 9)

type FprQueue = { (...args: unknown[]): void; q?: unknown[][] };

let initialized = false;

export function initFirstPromoter(): void {
  if (typeof window === 'undefined' || initialized) return;
  const cid = import.meta.env.VITE_FIRSTPROMOTER_CID;
  if (!cid) return;

  const w = window as Window & { fpr?: FprQueue };
  // Queue shim — fpr.js replaces w.fpr and drains w.fpr.q once it loads.
  if (!w.fpr) {
    const fpr: FprQueue = (...args: unknown[]) => {
      fpr.q = fpr.q || [];
      if (args[0] === 'set') fpr.q.unshift(args);
      else fpr.q.push(args);
    };
    w.fpr = fpr;
  }
  w.fpr('init', { cid });
  w.fpr('click');

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://cdn.firstpromoter.com/fpr.js';
  document.head.appendChild(script);

  initialized = true;
}
